'use client';

import { c as _c } from "react/compiler-runtime";
import { convictionPercent, parseMarketConfig, useGetMarketField, useIntentPrefetch, useMarketConfig } from '@morfi/backend-sdk-v2/client';
import { isMarketResolved } from '@morfi/contract-sdk';
import { useTranslations } from 'next-intl';
import { OptimizedImage } from '@/components/images/optimized-image';
import { ThumbWithOrigin } from '@/components/market/identity/identity-marks';
import type { MarketIdentityData } from '@/components/market/identity/market-identity';
import { EntityTag } from '@/components/search/entity-tag';
import { RowAside, TickerText } from '@/components/search/search-row-parts';
import { tickerLabel } from '@/components/search/search-row-parts.utils';
import { SearchRowSubline } from '@/components/search/search-row-subline';
import { useCommandLink } from '@/components/search/use-command-link';
import { CommandItem } from '@/components/ui/app-command';
import { formatMarketStatus } from '@/config/market-display';
import { useFormattedCurrency } from '@/lib/currency';
interface MarketRowSearchItemProps extends MarketIdentityData {
  pubkey: string;
  title: string;
  coverImageUrl: string;
  coverImageNsfw: boolean;
  status: string;
  marketConfig: string | undefined;
  volumeTotal: string;
  prizePool: string;
  numOutcomes: number;
  ticker: string;
  /** Highlight the ticker (used in `$ticker` search mode). */
  tickerHighlighted?: boolean;
  /** When true the row is the single CA exact-match (brand outline). */
  exact?: boolean;
  href: string;
  onCommit: () => void;
  onHoverPrefetch?: (pubkey: string) => void;
}
function shouldShowNumeric(value: string | undefined): boolean {
  if (value === undefined || value === '' || value === '0') return false;
  try {
    return BigInt(value) > BigInt(0);
  } catch {
    // SILENT-CATCH: non-numeric strings collapse the metric fragment
    return false;
  }
}
export function MarketRowSearchItem(props) {
  const $ = _c(55);
  const config = useMarketConfig(props.marketConfig);
  let t0;
  if ($[0] !== props.pubkey) {
    t0 = {
      pubkey: props.pubkey
    };
    $[0] = props.pubkey;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const detailArgs = t0;
  const liveVolume = useGetMarketField(detailArgs, "volumeTotal");
  const liveStatus = useGetMarketField(detailArgs, "status");
  const livePrizePool = useGetMarketField(detailArgs, "prizePool");
  const tMarketRow = useTranslations("market.row");
  const tMarket = useTranslations("market");
  const tSearch = useTranslations("search");
  const {
    formatD6
  } = useFormattedCurrency();
  const status = liveStatus ?? props.status;
  const volume = liveVolume ?? props.volumeTotal;
  const prizePool = livePrizePool ?? props.prizePool;
  let t1;
  bb0: {
    if (isMarketResolved(status)) {
      let t2;
      if ($[2] !== status || $[3] !== tMarket) {
        t2 = formatMarketStatus(status, tMarket);
        $[2] = status;
        $[3] = tMarket;
        $[4] = t2;
      } else {
        t2 = $[4];
      }
      t1 = t2;
      break bb0;
    }
    if (config?.resolutionMode === "conviction" && config.convictionResolutionThresholdPpb !== null) {
      let t2;
      if ($[5] !== config) {
        t2 = Math.round(convictionPercent(parseMarketConfig(config).convictionResolutionThresholdPpb));
        $[5] = config;
        $[6] = t2;
      } else {
        t2 = $[6];
      }
      const pct = t2;
      const t3 = `${String(pct)}%`;
      let t4;
      if ($[7] !== t3 || $[8] !== tMarketRow) {
        t4 = tMarketRow("resolves_at", {
          value: t3
        });
        $[7] = t3;
        $[8] = tMarketRow;
        $[9] = t4;
      } else {
        t4 = $[9];
      }
      t1 = t4;
      break bb0;
    }
    t1 = null;
  }
  const leading = t1;
  let t2;
  if ($[10] !== props.numOutcomes || $[11] !== tSearch) {
    t2 = props.numOutcomes > 0 ? tSearch("outcomes_count", {
      count: props.numOutcomes
    }) : null;
    $[10] = props.numOutcomes;
    $[11] = tSearch;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const outcomesPiece = t2;
  let t3;
  if ($[13] !== formatD6 || $[14] !== tMarketRow || $[15] !== volume) {
    t3 = shouldShowNumeric(volume) ? tMarketRow("traded", {
      value: formatD6(volume, "compact")
    }) : null;
    $[13] = formatD6;
    $[14] = tMarketRow;
    $[15] = volume;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  const tradedPiece = t3;
  let t4;
  if ($[17] !== props) {
    t4 = () => {
      props.onHoverPrefetch?.(props.pubkey);
    };
    $[17] = props;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const handleHover = t4;
  const intent = useIntentPrefetch(handleHover);
  const {
    onSelect,
    overlay
  } = useCommandLink(props.href, props.onCommit);
  const t5 = props.exact === true ? "true" : undefined;
  const t6 = props.coverImageUrl.length > 0 ? props.coverImageUrl : null;
  let t7;
  if ($[19] !== props.coverImageNsfw || $[20] !== props.pubkey || $[21] !== t6) {
    t7 = <OptimizedImage variant="thumbnail" size="size-8" entity={{
      kind: "market",
      pubkey: props.pubkey,
      category: "",
      coverImageUrl: t6,
      nsfw: props.coverImageNsfw
    }} alt="" preview={false} />;
    $[19] = props.coverImageNsfw;
    $[20] = props.pubkey;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== props.marketType || $[24] !== t7) {
    t8 = <ThumbWithOrigin marketType={props.marketType} size="sm">{t7}</ThumbWithOrigin>;
    $[23] = props.marketType;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== props.title) {
    t9 = <span className="truncate text-body font-medium">{props.title}</span>;
    $[26] = props.title;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <EntityTag kind="market" />;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== t9) {
    t11 = <div className="flex min-w-0 items-center gap-2">{t9}{t10}</div>;
    $[29] = t9;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== props.ticker || $[32] !== props.tickerHighlighted) {
    t12 = tickerLabel(props.ticker) !== null ? <TickerText key="ticker" ticker={props.ticker} highlighted={props.tickerHighlighted} /> : null;
    $[31] = props.ticker;
    $[32] = props.tickerHighlighted;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== leading || $[35] !== outcomesPiece || $[36] !== t12 || $[37] !== tradedPiece) {
    t13 = <SearchRowSubline pieces={[t12, leading, outcomesPiece, tradedPiece]} />;
    $[34] = leading;
    $[35] = outcomesPiece;
    $[36] = t12;
    $[37] = tradedPiece;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== t11 || $[40] !== t13) {
    t14 = <div className="flex min-w-0 flex-1 flex-col gap-0.5">{t11}{t13}</div>;
    $[39] = t11;
    $[40] = t13;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== formatD6 || $[43] !== prizePool || $[44] !== tSearch) {
    t15 = shouldShowNumeric(prizePool) ? <RowAside value={formatD6(prizePool, "compact")} label={tSearch("label_prize_pool")} /> : null;
    $[42] = formatD6;
    $[43] = prizePool;
    $[44] = tSearch;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  let t16;
  if ($[46] !== intent || $[47] !== onSelect || $[48] !== overlay || $[49] !== props.pubkey || $[50] !== t14 || $[51] !== t15 || $[52] !== t5 || $[53] !== t8) {
    t16 = <CommandItem value={props.pubkey} onSelect={onSelect} {...intent} className="relative gap-3 rounded-overlay-item px-3 py-2.5 data-[exact=true]:border data-[exact=true]:border-border-brand-strong" data-exact={t5}>{overlay}{t8}{t14}{t15}</CommandItem>;
    $[46] = intent;
    $[47] = onSelect;
    $[48] = overlay;
    $[49] = props.pubkey;
    $[50] = t14;
    $[51] = t15;
    $[52] = t5;
    $[53] = t8;
    $[54] = t16;
  } else {
    t16 = $[54];
  }
  return t16;
}